import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { PcmpProjectFormValues } from '../../types/pcmpProjectForm.schema';
import type { PcmpCatalogItem } from '../../types/pcmpProject.types';
import { pcmpPriceKey } from '../../utils/pcmpPriceGrid';
import { PcmpCategoryQuickAddModal } from './PcmpCategoryQuickAddModal';
import { PcmpFormSection } from './PcmpFormSection';

interface PcmpPriceGridSectionProps {
  categories: PcmpCatalogItem[];
  classes: PcmpCatalogItem[];
  /** Grid cells a submission uses: their row and column cannot be removed. */
  lockedKeys: ReadonlySet<string>;
}

function lockedIds(lockedKeys: ReadonlySet<string>, part: 0 | 1): Set<number> {
  return new Set([...lockedKeys].map((key) => Number(key.split(':')[part])));
}

/** A row per category and a column per class; every cell needs a price. */
export function PcmpPriceGridSection({
  categories,
  classes,
  lockedKeys,
}: PcmpPriceGridSectionProps) {
  const {
    control,
    register,
    setValue,
    formState: { errors, isSubmitted },
  } = useFormContext<PcmpProjectFormValues>();
  const categoryIds = useWatch({ control, name: 'categoryIds' });
  const classIds = useWatch({ control, name: 'classIds' });
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  const categoryById = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories],
  );
  const lockedCategoryIds = useMemo(() => lockedIds(lockedKeys, 0), [lockedKeys]);
  const lockedClassIds = useMemo(() => lockedIds(lockedKeys, 1), [lockedKeys]);

  const selectedClasses = classes.filter((academicClass) => classIds.includes(academicClass.id));
  const addableCategories = categories.filter((category) => !categoryIds.includes(category.id));
  const setOptions = { shouldDirty: true, shouldValidate: isSubmitted } as const;

  function toggleClass(classId: number) {
    if (lockedClassIds.has(classId)) return;
    setValue(
      'classIds',
      classIds.includes(classId) ? classIds.filter((id) => id !== classId) : [...classIds, classId],
      setOptions,
    );
  }

  function addCategory(categoryId: number | undefined) {
    if (categoryId === undefined || categoryIds.includes(categoryId)) return;
    setValue('categoryIds', [...categoryIds, categoryId], setOptions);
  }

  function removeCategory(categoryId: number) {
    setValue(
      'categoryIds',
      categoryIds.filter((id) => id !== categoryId),
      setOptions,
    );
  }

  const cellErrors = errors.prices ?? {};
  const hasCellErrors = Object.keys(cellErrors).length > 0;

  return (
    <PcmpFormSection
      id="pcmp-project-price-grid-heading"
      title="Book Prices"
      description="Pick the classes, add a row for each category, and enter the book price of every cell."
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-medium">
          Classes <span className="text-destructive">*</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {classes.map((academicClass) => {
            const isSelected = classIds.includes(academicClass.id);
            const isLocked = lockedClassIds.has(academicClass.id);
            return (
              <Button
                key={academicClass.id}
                type="button"
                size="sm"
                variant={isSelected ? 'default' : 'outline'}
                aria-pressed={isSelected}
                disabled={isLocked}
                title={isLocked ? 'Used by submissions; cannot be removed' : undefined}
                onClick={() => toggleClass(academicClass.id)}
                className="gap-1"
              >
                {isLocked ? <Lock className="size-3" /> : null}
                {academicClass.name}
              </Button>
            );
          })}
        </div>
        {errors.classIds?.message ? (
          <p role="alert" className="text-xs text-destructive">
            {errors.classIds.message}
          </p>
        ) : null}
      </fieldset>

      {categoryIds.length > 0 && selectedClasses.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 min-w-44 bg-muted px-3 py-2 text-left font-medium"
                >
                  Category
                </th>
                {selectedClasses.map((academicClass) => (
                  <th
                    key={academicClass.id}
                    scope="col"
                    className="min-w-28 px-2 py-2 text-right font-medium whitespace-nowrap"
                  >
                    {academicClass.name}
                  </th>
                ))}
                <th scope="col" className="w-12 px-2 py-2">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {categoryIds.map((categoryId) => {
                const category = categoryById.get(categoryId);
                const categoryName = category?.name ?? `Category ${categoryId}`;
                const isLocked = lockedCategoryIds.has(categoryId);
                return (
                  <tr key={categoryId} className="border-t">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-medium"
                    >
                      {categoryName}
                    </th>
                    {selectedClasses.map((academicClass) => {
                      const key = pcmpPriceKey(categoryId, academicClass.id);
                      const error = cellErrors[key]?.message;
                      return (
                        <td key={academicClass.id} className="px-2 py-1.5">
                          <Input
                            {...register(`prices.${key}`)}
                            inputMode="decimal"
                            placeholder="0.00"
                            aria-label={`${categoryName}, ${academicClass.name} price`}
                            aria-invalid={error ? true : undefined}
                            title={error}
                            className={cn('h-9 text-right tabular-nums')}
                          />
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={isLocked}
                        title={
                          isLocked
                            ? 'Used by submissions; cannot be removed'
                            : `Remove ${categoryName}`
                        }
                        aria-label={`Remove ${categoryName}`}
                        onClick={() => removeCategory(categoryId)}
                      >
                        {isLocked ? <Lock className="size-4" /> : <Trash2 className="size-4" />}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {hasCellErrors ? (
        <p role="alert" className="text-xs text-destructive">
          Enter a price (up to 9,999,999.99, two decimals) in every highlighted cell.
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <OptionSelect
          id="pcmp-project-add-category"
          options={addableCategories}
          value={undefined}
          onChange={addCategory}
          placeholder={addableCategories.length > 0 ? 'Add a category row' : 'All categories added'}
          disabled={addableCategories.length === 0}
          className="h-10 sm:w-72"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsQuickAddOpen(true)}
          className="h-10 gap-1.5"
        >
          <Plus className="size-4" />
          New Category
        </Button>
      </div>
      {errors.categoryIds?.message ? (
        <p role="alert" className="text-xs text-destructive">
          {errors.categoryIds.message}
        </p>
      ) : null}

      <PcmpCategoryQuickAddModal
        open={isQuickAddOpen}
        onOpenChange={setIsQuickAddOpen}
        onCreated={(category) => addCategory(category.id)}
      />
    </PcmpFormSection>
  );
}
